import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Download, Upload } from "lucide-react";
import { toast } from "react-toastify";
import axiosInstance from "../../utils/axios/axiosConfig";

function ImportModal({ isOpen, onClose, setDone, done }) {
  const [formDataImport, setFormDataImport] = useState({
    file: null,
    brand_id: null,
  });
  const [loading, setLoading] = useState(false);

  const handleChangeImport = (e) => {
    const { name, type, files, value } = e.target;
    if (type === "file") {
      const file = files?.[0];
      if (!file) return;

      const validExtensions = [".xls", ".xlsx"];
      const validMimeTypes = [
        "application/vnd.ms-excel",
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      ];

      const isValidExtension = validExtensions.some((ext) =>
        file.name.toLowerCase().endsWith(ext)
      );
      const isValidMimeType = validMimeTypes.includes(file.type);

      if (!isValidExtension && !isValidMimeType) {
        toast.error("Please upload a valid Excel file (.xls or .xlsx).");
        return;
      }

      setFormDataImport((prev) => ({ ...prev, [name]: file }));
    } else {
      setFormDataImport((prev) => ({ ...prev, [name]: value }));
    }
  };

  const saveImport = async (e) => {
    e.preventDefault();

    if (!formDataImport.file) {
      toast.warning("Please select a file.");
      return;
    }

    try {
      setLoading(true);
      const formData = new FormData();
      formData.append("file", formDataImport.file);

      const response = await axiosInstance.post(
        "/brands/importBrands",
        formData
      );
      const responseData = response.data;

      if (responseData) {
        toast.success(responseData.message);
        onClose();
        setDone(!done);
      }
    } catch (error) {
      toast.error(
        error.response?.data?.message || "Error occurred during file upload."
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-50 grid place-items-center bg-black/60">
          <motion.div
            key="import-brands"
            initial={{ opacity: 0, scale: 0.94 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.94 }}
            transition={{ duration: 0.22 }}
            className="w-full max-w-xl overflow-hidden rounded-2xl bg-surface shadow-2xl"
            role="dialog"
            aria-modal="true"
          >
            {/* header */}
            <div className="flex items-center justify-between border-b border-line px-6 py-4">
              <h2 className="text-lg font-semibold text-ink">
                Import Brands
              </h2>
              <button
                type="button"
                onClick={onClose}
                className="rounded-full p-2 hover:bg-surface-2"
                aria-label="Close"
              >
                <X className="h-5 w-5 text-ink" />
              </button>
            </div>

            {/* body */}
            <form onSubmit={saveImport} className="px-6 py-5 text-start">
              <div className="mb-6">
                <label
                  htmlFor="file"
                  className="mb-2 block text-sm font-medium text-ink"
                >
                  Excel File
                </label>

                <label
                  htmlFor="file"
                  className="flex cursor-pointer items-center justify-between rounded-lg border border-dashed border-line bg-surface-2 px-4 py-4 hover:bg-surface-2"
                  title="Upload .xls or .xlsx"
                >
                  <div className="flex items-center gap-3">
                    <Upload className="h-5 w-5 text-accent" />
                    <div className="text-sm">
                      <div className="font-medium text-ink">
                        {formDataImport.file?.name || "Choose Excel file"}
                      </div>
                      <div className="text-xs text-muted">
                        Accepted: .xls, .xlsx
                      </div>
                    </div>
                  </div>
                  <span className="rounded-md bg-surface px-3 py-1.5 text-sm text-ink ring-1 ring-neutral-300">
                    Browse
                  </span>
                </label>

                <input
                  id="file"
                  name="file"
                  type="file"
                  accept=".xls,.xlsx,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                  onChange={handleChangeImport}
                  className="sr-only"
                />
              </div>

              {/* footer */}
              <div className="flex items-center justify-end gap-2 border-t border-line px-1 pt-4">
                <button
                  type="button"
                  onClick={onClose}
                  className="rounded-lg px-4 py-2 text-sm font-medium text-ink hover:bg-surface-2"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="inline-flex items-center gap-2 rounded-lg bg-accent px-4 py-2 text-sm font-semibold text-white transition hover:bg-accent-strong focus:outline-none focus:ring-2 focus:ring-accent disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {loading ? (
                    <div
                      className="inline-block size-5 animate-spin rounded-full border-2 border-white border-t-transparent"
                      role="status"
                      aria-label="loading"
                    />
                  ) : (
                    <Download className="h-4 w-4" />
                  )}
                  Import
                </button>
              </div>
            </form>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
}

export default ImportModal;
